import React, { useState, useEffect } from 'react';
import "./InventoryTransferForm.css";

const InventoryTransferForm = ({
  onClose,
  inventory = [],
  locations = [],
  onTransferComplete,
  fetchInventory,
  userId
}) => {
  const [selectedInventory, setSelectedInventory] = useState([]); // selected inventory items
  const [searchQuery, setSearchQuery] = useState(''); // search query
  const [newLocationId, setNewLocationId] = useState(''); // selected destination location ID

  // 🔄 Refresh inventory whenever component mounts
  useEffect(() => {
    if (fetchInventory) fetchInventory();
  }, [fetchInventory]);

  // ✅ Toggle item selection
  const handleInventorySelection = (item_code) => {
    setSelectedInventory((prevSelectedInventory) =>
      prevSelectedInventory.includes(item_code)
        ? prevSelectedInventory.filter(code => code !== item_code)
        : [...prevSelectedInventory, item_code]
    );
  };

  // ✅ Submit handler for transfer
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedInventory.length === 0) {
      alert('Please select at least one inventory item to transfer.');
      return;
    }

    if (!newLocationId) {
      alert('Please select a destination location.');
      return;
    }

    const transferData = {
      item_code: selectedInventory,
      newLocationId, // must be numeric location_id
      user_id: userId,
    };

    console.log("Submitting transfer payload:", transferData);

    try {
      const res = await fetch('/api/inventory/transfer', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transferData),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error('Transfer failed:', data);
        alert(data.error || 'Failed to transfer items.');
        return;
      }

      console.log('Transfer successful:', data);
      alert('Inventory transferred successfully.');
      onTransferComplete?.(); // refresh parent data
      fetchInventory?.(); // refresh inventory list
      onClose?.(); // close modal
    } catch (error) {
      console.error('Error during transfer:', error);
      alert('Server error — please try again.');
    }
  };

  // 🔍 Filter inventory by name, code, or serial number
  const filteredInventory = inventory.filter((item) =>
    (item.item_first_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.item_second_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.item_code?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (item.serial_number?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {/* Close Button */}
        <button className="close-button" onClick={onClose}>✖</button>

        <form onSubmit={handleSubmit}>
          <h3>Transfer Inventory from Location A to Location B</h3>

          {/* Search Input */}
          <div className='form-group'>
            <label id="add-user-label">Search Inventory Items:</label>
            <input
              id="add-user-input"
              type="text"
              placeholder="Search by name, product code, or serial number"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Inventory Table */}
          <div className="inventory-selection-container">
            <table>
              <thead>
                <tr>
                  <th className='first-child'>Select</th>
                  <th>First Name</th>
                  <th>Second Name</th>
                  <th>Product Code</th>
                  <th>Serial Number</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length > 0 ? (
                  filteredInventory.map((item) => (
                    <tr key={item.item_code}>
                      <td className='first-child'>
                        <input
                          type="checkbox"
                          value={item.item_code}
                          checked={selectedInventory.includes(item.item_code)}
                          onChange={() => handleInventorySelection(item.item_code)}
                        />
                      </td>
                      <td>{item.item_first_name}</td>
                      <td>{item.item_second_name}</td>
                      <td>{item.item_code}</td>
                      <td>{item.serial_number}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5">No inventory items match your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Destination Location Dropdown */}
          <div className='form-group'>
            <label id="add-user-label">Select Destination Location:</label>
            <select
              id="add-user-input"
              value={newLocationId}
              onChange={(e) => setNewLocationId(e.target.value)}
              required
            >
              <option value="">Select a location</option>
              {locations.map((location) => (
                <option key={location.location_id} value={location.location_id}>
                  {location.location_name}
                </option>
              ))}
            </select>
          </div>

          {/* Submit */}
          <button type="submit" className="submit-btn">Transfer Inventory</button>
        </form>
      </div>
    </div>
  );
};

export default InventoryTransferForm;
